import React, {useState} from "react";
import apiHelper from "../../../util/ApiHelper";
import {Link} from "react-router-dom";
import {useLoading} from "../../../util/LoadingProvider";
import {Box, Grid, Typography} from "@material-ui/core";
import {toast} from "react-toastify";
import {modal} from "../../../util";
import FilmCategoryForm from "./filmCategoryForm";
import DragAndDropTable
    from "../../common/DragAndDropTable";

const formDefaultValues = (seq) => ({
    id: 0,
    backgroundColor: "#000000",
    seq,
    status: true,
    categoryId: -1,
    localizations: {
        en: {
            name: ""
        },
        tc: {
            name: ""
        }
    }
})

export default function FilmCategoryPage(props) {
    const {disabled, match} = props;
    const [globalFilter, setGlobalFilter] = useState("");
    const [isSearched, setIsSearched] = useState(false);
    const [orderingDisabled, setOrderingDisabled] = useState(false);
    const [rows, setRows] = useState([]);
    const [searchString, setSearchString] = useState("");
    const {setLoading} = useLoading();
    const [pageSize, setPageSize] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const {catId} = match.params;
    const [paramId, setParamId] = useState(catId);
    const [formDefaultData, setFormDefaultData] = useState(formDefaultValues(0));
    const [categorySelection, setCategorySelection] = useState([]);

    const getCategorySelection = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getFilmCategories(
                null,
                0,
                0,
                9999999,
                ''
            );
            console.log('getCategorySelection: ', result);
            // for placing new records at the top of listing
            result.data.content[0]
            && !isNaN(result.data.content[0].seq)
            && setFormDefaultData(formDefaultValues(result.data.content[0].seq + 1));
            setCategorySelection(result.data.content.map((category) => (
                {
                    id: category.id,
                    localizations: {
                        en: {
                            name: category.localizations.en.name
                        }
                    }
                }
            )));
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [setLoading])

    const getCategories = React.useCallback(async () => {
        try {
            setLoading(true);
            const result =
                paramId === undefined ?
                    await apiHelper.hkiff.getFilmCategories(
                        null,
                        pageIndex,
                        (pageIndex + 1) * pageSize,
                        pageSize,
                        searchString
                    ) :
                    await apiHelper.hkiff.getFilmSubCategories(
                        paramId,
                        null,
                        pageIndex,
                        (pageIndex + 1) * pageSize,
                        pageSize,
                        searchString
                    )
            ;
            console.log('getCategories: ', result);
            setRows(result.data.content.filter((x) => x));
            setTotalElements(result.data.totalElements);
            setTotalPages(result.data.totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [pageIndex, pageSize, searchString, setLoading]);

    const getCategoryById = React.useCallback(async () => {
        try {
            setLoading(true);
            if (paramId !== undefined) {
                const result = await apiHelper.hkiff.getFilmCategoryById(paramId);
                console.log('getCategoryById: ', result);
                setFormDefaultData({...result.data, backgroundColor: result.data.backgroundColor ? `#${result.data.backgroundColor}` : null});
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [paramId])

    const createNewCategory = async (data) => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.createFilmCategory(data);
            console.log(result);
            if (result.data.id) {
                toast.dark("Your changes are saved.");
                getCategorySelection();
                getCategories();
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }

    const createNewSubCategory = async (data, callback) => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.createFilmSubCategory(data);
            console.log(result);
            if (result.data.id) {
                toast.dark("Your changes are saved.");
                callback && callback();
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }

    const updateCategory = async (id, data) => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.updateFilmCategoryById(id, data);
            if (result.data.id) {
                toast.dark("Your changes are saved.");
            }
            console.log(result);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }

    const submitApi = (data, callback) => {
        data.backgroundColor = data.backgroundColor ? data.backgroundColor.replace('#', '') : null;
        if (paramId === undefined) {
            if (data.categoryId === -1) {
                const {categoryId, ...rest} = data;
                createNewCategory(rest);
            } else {
                createNewSubCategory(data, callback);
            }
        } else {
            const {categoryId, ...rest} = data;
            updateCategory(data.id, rest);
        }
    }

    React.useEffect(() => {
        if (pageSize) getCategories();
    }, [getCategories]);

    React.useEffect(() => {
        if (paramId === undefined) {
            getCategorySelection();
        } else {
            getCategoryById();
        }
    }, [getCategoryById, getCategorySelection, paramId])

    const handleSorting = (bool) => {
        if (!isSearched) {
            setOrderingDisabled(bool);
        }
    };

    const handleSearch = (searchString) => {
        setGlobalFilter(searchString);
        setIsSearched(true);
        setOrderingDisabled(true);
    };

    const saveOrdering = async () => {
        try {
            const seqData = {};
            rows.forEach((e, i) => {
                seqData[e.id] = i + 1;
            });
            let result;
            if (paramId === undefined) {
                result = await apiHelper.hkiff.orderFilmCategories(seqData);
            } else {
                result = await apiHelper.hkiff.orderFilmSubCategories(seqData);
            }

            if (result.data.code === 0) {
                toast.dark("Your changes is saved.");
                await getCategories();
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleDelete = async (row) => {
        const decision = await modal.fire({
            title: "Delete Category",
            text: "Are you sure to delete this category?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const {id} = row;
                setLoading(true);
                const result =
                    paramId === undefined ?
                        await apiHelper.hkiff.deleteFilmCategoryById(id) :
                        await apiHelper.hkiff.deleteFilmSubCategoryById(id)
                ;
                if (result.data.code === 0) {
                    toast.dark("The category is deleted.");
                    await getCategories();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const handleBulkDeleteCategory = async (ids) => {
        if (ids.length > 0) {
            setLoading(true);
            if (paramId === undefined) {
                Promise.all(ids.map((id) => apiHelper.hkiff.deleteFilmCategoryById(id)))
                .then((result)=>{
                    setLoading(false);
                    console.log('result: ', result);
                    if (!Array.isArray(result)) result = [result];
                    if (result.every(res => res.data.code === 0)) {
                        getCategories();
                        toast.dark("The categories are deleted.");
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            } else {
                Promise.all(ids.map((id) => apiHelper.hkiff.deleteFilmSubCategoryById(id)))
                .then((result)=>{
                    setLoading(false);
                    console.log('result: ', result);
                    if (!Array.isArray(result)) result = [result];
                    if (result.every(res => res.data.code === 0)) {
                        getCategories();
                        toast.dark("The categories are deleted.");
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            }
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Id",
                accessor: "id",
                Cell: ({row}) => {
                    return (
                        <Link
                            style={{
                                textDecoration: "none",
                                color: "#306F9D",
                            }}
                            to={`${match.url}/${row.original.id}`}
                        >
                            {row.original.id}
                        </Link>
                    );
                },
            },
            {
                Header: "Name (English)",
                accessor: "localizations.en.name",
                Cell: ({row}) => {
                    return (
                        <Link
                            style={{
                                textDecoration: "none",
                                color: "#306F9D",
                            }}
                            to={`${match.url}/${row.original.id}`}
                        >
                            {row.original.localizations.en.name}
                        </Link>
                    );
                },
            },
            {
                Header: "Name (Traditional Chinese)",
                accessor: "localizations.tc.name",
                Cell: ({row}) => {
                    return (
                        <Link
                            style={{
                                textDecoration: "none",
                                color: "#306F9D",
                            }}
                            to={`${match.url}/${row.original.id}`}
                        >
                            {row.original.localizations.tc.name}
                        </Link>
                    );
                },
            },
            {
                Header: "Background Color",
                accessor: "backgroundColor",
                Cell: ({row}) => {
                    return (
                        row.original.backgroundColor ?
                        <div style={{
                            width: "60px",
                            height: "30px",
                            backgroundColor: `#${row.original.backgroundColor}`
                        }}></div> :
                        <span>null</span>
                    );
                },
                disableGlobalFilter: true,
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ row }) => {
                    if (row.original.status) {
                        return <span style={{color: "#306F9D"}}>Active</span>
                    } else {
                        return <span style={{color: "#306F9D"}}>Inactive</span>
                    }
                },
                disableGlobalFilter: true,
            },
        ],
        []
    );

    return (
        <>
            <Box width="100%">
                <Typography variant="h4" gutterBottom>
                    Film > Film Category {paramId !== undefined && '> Subcategory Detail'}
                </Typography>
            </Box>
            <Grid container spacing={4}>
                <Grid item xs={4}>
                    <FilmCategoryForm
                        title={paramId === undefined ? 'Add New Category' : 'Edit Category'}
                        buttonLabel={paramId === undefined ? 'Add New Category' : 'Save Changes'}
                        categorySelectArray={categorySelection}
                        shouldCategoryIncludeMain={true}
                        shouldCategoryUseMain={paramId !== undefined}
                        defaultData={formDefaultData}
                        submitApi={submitApi}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={8}>
                    <DragAndDropTable
                        globalFilter={globalFilter}
                        orderingDisabled={orderingDisabled}
                        handleSorting={handleSorting}
                        handleSearch={handleSearch}
                        searchString={searchString}
                        setSearchString={setSearchString}
                        fetchData={getCategories}
                        data={rows}
                        setData={setRows}
                        columns={columns}
                        bulkDeleteApi={handleBulkDeleteCategory}
                        subject="category"
                        saveOrderingApi={saveOrdering}
                        disabled={disabled}
                        withPagination={true}
                        editDeleteAction={true}
                        handleDelete={handleDelete}
                        deleteByRow={true}
                        setParentPageSize={setPageSize}
                        setParentPageIndex={setPageIndex}
                        parentPageIndex={pageIndex}
                        totalElements={totalElements}
                        totalPages={totalPages}
                    />
                </Grid>
            </Grid>
        </>
    );
}
